import { useId, useState, type ReactNode } from 'react';
import { useTableMetrics } from '@/hooks/useTableMetrics';
import { TableScrollRail } from '../ui/TableScrollRail';
import { money, invoiceBalanceCents, balanceLabel } from '../money';
import { StatusBadge } from '../ui/Badge';
import type { LineBadge } from './lineNotes';

/**
 * Line items + totals, rendered ONE way for both quotes and invoices. They are the same
 * shape in the database (one set of columns, two tables) and a customer reads them as the
 * same document, so they must not drift into two lookalike tables on screen.
 */
export interface DisplayLine {
  id: string;
  name: string;
  description: string | null;
  qty: string;
  unitPriceCents: number;
  totalCents: number;
  /**
   * Invoices only (Inventory v2 12b): the part line this discount line prints under ("Price
   * adjusted to $38.00 each", "Discount 50%"). Drawn indented beneath it, its amount only.
   */
  adjustsLineId?: string | null;
}

export function LineItemsTable({
  lines,
  subtotalCents,
  taxCents,
  totalCents,
  paidCents,
  lateFeeCents = 0,
  badges = {},
  unpriced = [],
  action,
}: {
  lines: DisplayLine[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  /** Invoices only — shows what's been paid and what's left. */
  paidCents?: number;
  /** Invoices only — fees charged since issue. They sit BELOW the total, because the total
   *  is the frozen document and the fee is what happened to it afterwards. */
  lateFeeCents?: number;
  /**
   * Short notes to show beside a line, keyed by line id (an invoice's "Part used · Devon ·
   * Today", "No price"). Words and tones the caller has already decided; the table only draws them.
   */
  badges?: Record<string, LineBadge[]>;
  /** Lines whose unit price is unknown: the Unit reads "No price", never "$0.00". */
  unpriced?: readonly string[];
  /** A control beside a line's name, when the caller offers one ("Back to job" on a draft). */
  action?: (line: DisplayLine) => ReactNode;
}) {
  const scrollerId = useId();
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const metrics = useTableMetrics(
    scroller,
    '["item","qty","unit","total"]',
    'thead th:last-child',
  );
  // A shop's amounts determine the width, not a guessed phone breakpoint.
  const pinTotal =
    metrics.columnWidth > 0 && metrics.columnWidth <= metrics.clientWidth / 2;
  const footerLabel = pinTotal ? 'sticky left-0 z-[1] inline-block bg-surface' : '';
  const amountCell = pinTotal
    ? 'sticky right-0 z-[2] bg-surface pl-3 whitespace-nowrap shadow-[-1px_0_0_var(--sw-border)]'
    : 'pl-3 whitespace-nowrap';

  if (lines.length === 0) {
    return <p className="text-sm text-muted-foreground">No line items yet.</p>;
  }

  return (
    <>
      <div
        ref={setScroller}
        id={scrollerId}
        role="region"
        aria-label="Line items and totals"
        tabIndex={0}
        className="h-scroll-silent"
        style={pinTotal ? { scrollPaddingRight: metrics.columnWidth } : undefined}
      >
        <table className="w-full min-w-[420px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="pb-2 font-medium">Item</th>
              <th className="pb-2 text-right font-medium">Qty</th>
              <th className="pb-2 text-right font-medium">Unit</th>
              <th scope="col" className={`${amountCell} pb-2 text-right font-medium`}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) =>
              l.adjustsLineId ? (
                // What came off the part above: indented, the amount only.
                <tr key={l.id} className="border-b border-border last:border-b-0">
                  <td className="py-1 pl-4 text-[var(--sw-success-text)]" colSpan={3}>
                    {l.name}
                  </td>
                  <td
                    className={`${amountCell} py-1 text-right tabular-nums text-[var(--sw-success-text)]`}
                  >
                    {money(l.totalCents)}
                  </td>
                </tr>
              ) : (
                <tr key={l.id} className="border-b border-border last:border-b-0">
                  <td className="py-2">
                    <p className="flex flex-wrap items-center gap-x-2 font-medium">
                      {l.name}
                      {action?.(l)}
                    </p>
                    {badges[l.id]?.length ? (
                      <p className="mt-0.5 flex flex-wrap gap-1">
                        {badges[l.id]!.map((b) => (
                          <StatusBadge key={b.text} tone={b.tone}>
                            {b.text}
                          </StatusBadge>
                        ))}
                      </p>
                    ) : null}
                    {l.description ? (
                      <p className="text-xs text-muted-foreground">{l.description}</p>
                    ) : null}
                  </td>
                  <td className="py-2 text-right tabular-nums">{Number(l.qty)}</td>
                  <td className="py-2 text-right tabular-nums">
                    {unpriced.includes(l.id) ? (
                      <span className="text-[var(--sw-warn-text)]">No price</span>
                    ) : (
                      money(l.unitPriceCents)
                    )}
                  </td>
                  <td
                    className={`${amountCell} py-2 text-right font-medium tabular-nums`}
                  >
                    {money(l.totalCents)}
                  </td>
                </tr>
              ),
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border font-semibold">
              <td className="py-2" colSpan={3}>
                <span className={footerLabel}>Subtotal</span>
              </td>
              <td className={`${amountCell} py-2 text-right tabular-nums`}>
                {money(subtotalCents)}
              </td>
            </tr>
            {taxCents > 0 ? (
              <tr>
                <td className="pb-1" colSpan={3}>
                  <span className={footerLabel}>Tax</span>
                </td>
                <td className={`${amountCell} pb-1 text-right tabular-nums`}>
                  {money(taxCents)}
                </td>
              </tr>
            ) : null}
            <tr className="font-heading text-base font-bold">
              <td className="pt-1" colSpan={3}>
                <span className={footerLabel}>Total</span>
              </td>
              <td className={`${amountCell} pt-1 text-right tabular-nums`}>
                {money(totalCents)}
              </td>
            </tr>
            {lateFeeCents > 0 ? (
              <tr className="text-[var(--sw-danger-text)]">
                <td className="pt-1" colSpan={3}>
                  <span className={footerLabel}>Late fees</span>
                </td>
                <td className={`${amountCell} pt-1 text-right tabular-nums`}>
                  +{money(lateFeeCents)}
                </td>
              </tr>
            ) : null}
            {(paidCents !== undefined && paidCents > 0) || lateFeeCents > 0 ? (
              <>
                {paidCents !== undefined && paidCents > 0 ? (
                  <tr className="text-[var(--sw-success-text)]">
                    <td className="pt-1" colSpan={3}>
                      <span className={footerLabel}>Paid</span>
                    </td>
                    <td className={`${amountCell} pt-1 text-right tabular-nums`}>
                      −{money(paidCents)}
                    </td>
                  </tr>
                ) : null}
                <tr className="font-semibold">
                  <td className="pt-1" colSpan={3}>
                    <span className={footerLabel}>Balance</span>
                  </td>
                  <td className={`${amountCell} pt-1 text-right tabular-nums`}>
                    {balanceLabel(
                      invoiceBalanceCents({
                        totalCents,
                        lateFeeCents,
                        amountPaidCents: paidCents ?? 0,
                      }),
                    )}
                  </td>
                </tr>
              </>
            ) : null}
          </tfoot>
        </table>
      </div>
      <TableScrollRail scroller={scroller} controlsId={scrollerId} metrics={metrics} />
    </>
  );
}
